import { Anchor, Button, Stack } from "@flpstudio/design-system";
import { useState } from "react";
import { Link } from "react-router";

import { Skeleton } from "@/components/atoms/Loading/Skeleton";
import { ComponentError } from "@/components/organisms/Error/ComponentError";
import { useAuth } from "@/hooks/use-auth";
import { usePublishedDocument } from "@/hooks/use-documents";
import { DocumentMeta } from "@/screens/documentation/DocumentMeta";
import type { BestPracticeDocument } from "types";
import { paths } from "~/paths";
import { BestPracticeDocumentRenderer } from "./renderer/BestPracticeDocumentRenderer";
import {
  sendLinkPreviewClickEvent,
  sendSoftwareRecommendationClickEvent,
} from "./utils";

type Props = {
  className?: string;
  document: BestPracticeDocument;
};

export function PublishedDocumentPreview({ className, document }: Props) {
  const [viewFullDocument, setViewFullDocument] = useState(false);
  const { user } = useAuth();

  const { data, isLoading, isError } = usePublishedDocument(
    viewFullDocument ? document.urlTitle : "",
  );

  if (isLoading) {
    return <Skeleton repeat={5} />;
  }

  if (isError) {
    return <ComponentError />;
  }

  if (data) {
    return (
      <Stack component="article">
        <DocumentMeta document={data} />
        {data.content?.prompts.map((content) => (
          <BestPracticeDocumentRenderer
            key={content.id}
            content={content}
            currentTitleOrder={1}
            onLinkPreviewClick={(url) =>
              sendLinkPreviewClickEvent({
                document: data,
                user,
                url,
              })
            }
            onSoftwareRecommendationClick={(software) =>
              sendSoftwareRecommendationClickEvent({
                document: data,
                user,
                software,
              })
            }
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack component="article" className={className}>
      <DocumentMeta showSummary document={document} />
      {user ? (
        <Button
          variant="transparent"
          className="self-start border-none p-0 hover:underline"
          onClick={() => setViewFullDocument(true)}
        >
          Expand all
        </Button>
      ) : (
        <Anchor
          component={Link}
          className="self-start p-0 py-2 hover:underline"
          to={paths.signIn}
        >
          Expand all
        </Anchor>
      )}
    </Stack>
  );
}
